import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Layouts/Spinner";
import ReactSelect from "react-select";
import { Card, CardBody, Modal } from "reactstrap";
import * as yup from "yup";
import * as XLSX from "xlsx";
import { getUserList } from "../../../redux/invoice/action";
import { exportAllReports, getAllReports } from "../../../redux/service/action";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import { formatDateTime } from "../../Common/Utils/dateUtils";
import TestingAccountFilter from "../../Common/TestingAccountFilter";

export const invoiceReportValidation = () =>
  yup.object({
    start_date: yup.date(),
    end_date: yup
      .date()
      .min(yup.ref("start_date"), "End date cannot be before start date"),
    invoice_type: yup.number(),
    user_id: yup.string(),
  });

const InvoiceReport = () => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedOptionsMember, setSelectedOptionsMember] = useState([]);
  const [reportsData, setReportsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilter, setSHowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDates, setStartDates] = useState("");
  const [endDates, setEndDates] = useState("");
  const [exportLoader, setExportLoader] = useState(false);
  const [params, setParams] = useState({
    type: "invoice_report",
    search_key: "",
    status: "",
    page: 1,
    per_page: 10,
    testing_account_filter: "hide",
  });
  const [theFilterInfo, setTheFilterInfo] = useState({
    user_id: "",
    invoice_type: "",
    start_date: "",
    end_date: "",
    testing_account_filter: "hide",
    type: "invoice_report",
  });
  const userList = useSelector((state) => state?.reusableSlice?.userList);

  const dispatch = useDispatch();

  const invoiceOptions = [
    {
      value: 0,
      label: "Simplified",
    },
    {
      value: 1,
      label: "VAT",
    },
    {
      value: 2,
      label: "Purchase",
    },
  ];
  const proOptions = userList?.users?.map((item) => ({
    value: item?.id,
    label: item?.company_name,
  }));

  useEffect(() => {
    dispatch(getUserList({ search_key: "" }));
  }, [dispatch]);

  const handleChange = (selected) => {
    setSelectedOptions(selected);
    formik.setFieldValue("user_id", selected?.value);
  };

  const handleChangeMember = (selected) => {
    setSelectedOptionsMember(selected);
    formik.setFieldValue("invoice_type", selected?.value);
  };

  const handleStartDateChange = (event) => {
    setStartDates(event.target.value);
    formik.setFieldValue("start_date", event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDates(event.target.value);
    formik.setFieldValue("end_date", event.target.value);
  };

  const getDateTimeParts = (value) => {
    const formatted = formatDateTime(value, "");
    if (!formatted) return ["--", "--"];
    const normalized = String(formatted).trim();
    const parts = normalized.includes(", ")
      ? normalized.split(", ")
      : normalized.includes("T")
      ? normalized.split("T")
      : normalized.split(" ");
    const datePart = parts[0] || "--";
    const timePart = parts.slice(1).join(" ") || "--";
    return [datePart, timePart];
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: "",
      invoice_type: "",
      start_date: "",
      end_date: "",
      testing_account_filter: "hide",
      type: "invoice_report",
    },
    validationSchema: invoiceReportValidation,
    onSubmit: (values) => {
      if (
        values.user_id ||
        values.invoice_type || values.invoice_type === 0 ||
        values.start_date ||
        values.end_date ||
        values.testing_account_filter !== "hide"
      ) {
        handleSubmit(values);
      }
    },
  });

  const handleGetReports = (data) => {
    getAllReports(data)
      .then((res) => {
        if (res.data.status) {
          setIsLoading(false);
          setReportsData(res.data.response);
        } else {
          setIsLoading(false);
        }
      })
      .catch((e) => {
        console.log(e, "error in get report");
        setIsLoading(false);
      });
  };

  const handleExportReport = () => {
    setExportLoader(true);
    exportAllReports(theFilterInfo)
      .then((response) => {
        if (response.data) {
          setExportLoader(false);
          const header = [
            [
              "Sl",
              "Company Name",
              "Invoice Type",
              "Created Date",
              "Created Time",
              "No of Invoices",
            ],
          ];

          const data = response.data.invoice_report.map((prop, index) => {
            const [createdDate, createdTime] = getDateTimeParts(prop.created_on);
            return [
              index + 1,
              prop.company_name,
              prop.invoice_type === 0
              ? "Simplified"
              : prop.invoice_type === 1
              ? "VAT"
              : "Purchase",
              createdDate,
              createdTime,
              prop.invoice_count,
            ];
          });

          const worksheet = XLSX.utils.aoa_to_sheet(header); // Add headers
          XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A2" }); // Add data starting from row 2

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
          XLSX.writeFile(workbook, "InvoiceReport.xlsx");
        }
      })
      .catch((e) => {
        console.log(e, "while exporting reports");
        setExportLoader(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    handleGetReports(params);
  }, [params]);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    const data = {
      ...values,
      page: 1,
      per_page: 10,
    };
    setParams((preval) => ({
      ...preval,
      page: 1,
      ...values,
    }));
    setTheFilterInfo(values);
    handleGetReports(data);
  };

  const handleClearFilter = () => {
    if (
      formik.values.user_id ||
      formik.values.invoice_type || formik.values.invoice_type === 0||
      formik.values.start_date ||
      formik.values.end_date ||
      formik.values.testing_account_filter !== "hide"
    ) {
      setIsLoading(true);
      setSelectedOptions("");
      setStartDates("");
      setEndDates("");
      setSelectedOptionsMember("");
      formik.resetForm();
      setTheFilterInfo({
        user_id: "",
        invoice_type: "",
        start_date: "",
        end_date: "",
        testing_account_filter: "hide",
        type: "invoice_report",
      });
      setParams(() => ({
        type: "invoice_report",
        search_key: "",
        status: "",
        page: 1,
        per_page: 10,
        testing_account_filter: "hide",
      }));
      handleGetReports({
        user_id: "",
        start_date: "",
        end_date: "",
        membership_id: "",
        status: "",
        page: 1,
        per_page: 10,
        testing_account_filter: "hide",
        type: "invoice_report",
      });
    }
    return;
  };

  return (
    <Card className="">
      <div className="p-3">
        <div className="category-wrapper">
          <div className=" d-flex justify-content-between align-items-center w-100 mb-3">
            <h4>Invoice Report</h4>
            <div className="d-flex justify-content-between align-items-center ">
              <button
                type="button"
                className="submit-btn  filter-btn mx-5"
                onClick={() => setSHowFilter(!showFilter)}
              >
                <span>
                  <i className="fa-solid fa-filter"></i>{" "}
                  {showFilter ? "Hide Filter" : "Filter"}
                </span>
              </button>
              <button
                disabled={exportLoader}
                type="button"
                className="submit-btn"
                onClick={() => handleExportReport()}
                style={{ width: "max-content" }}
              >
                {exportLoader ? <Spinner /> : "Export"}
              </button>
            </div>
          </div>

          {showFilter && (
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-3 form-group">
                  <label>Select Company</label>
                  <ReactSelect
                    value={selectedOptions}
                    onChange={handleChange}
                    options={proOptions}
                    placeholder="select company"
                  />
                </div>

                <div className="col-md-3 form-group">
                  <label>Select Invoice Type</label>
                  <ReactSelect
                    value={selectedOptionsMember}
                    onChange={handleChangeMember}
                    options={invoiceOptions}
                    placeholder="select invoice type"
                  />
                </div>

                <div className="col-md-3 form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter start date"
                    value={startDates}
                    onChange={handleStartDateChange}
                    onBlur={formik.handleBlur}
                    name="start_date"
                  />
                  {formik.touched.start_date && formik.errors.start_date ? (
                    <div className="error">{formik.errors.start_date}</div>
                  ) : null}
                </div>

                <div className="col-md-3 form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter end date"
                    value={endDates}
                    onChange={handleEndDateChange}
                    onBlur={formik.handleBlur}
                    min={startDates || undefined}
                    name="end_date"
                  />
                  {formik.touched.end_date && formik.errors.end_date ? (
                    <div className="error">{formik.errors.end_date}</div>
                  ) : null}
                </div>
                <TestingAccountFilter
                  value={formik.values.testing_account_filter}
                  onChange={formik.handleChange}
                />
              </div>

              <div
                className="d-flex align-items-center justify-content-center mt-3  "
                style={{ columnGap: "20px" }}
              >
                <button
                  className="submit-btn   "
                  style={{ width: "max-content" }}
                  type="submit"
                >
                  {isLoading ?  <Spinner/> : "Submit" }

                </button>
                <button
                  type="button"
                  className="submit-btn  "
                  onClick={() => handleClearFilter()}
                  style={{ width: "max-content" }}
                >
                  Clear
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <div className="table-sec">
          <table className="table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Company Name</th>
                <th>Invoice Type</th>
                <th>Created Date</th>
                <th>Created Time</th>
                <th>No of Invoices</th>
                {/* <th className="text-center">Action</th> */}
              </tr>
            </thead>
            <tbody>
              {reportsData &&
              Array.isArray(reportsData.invoice_report) &&
              reportsData.invoice_report.length > 0 ? (
                reportsData.invoice_report.map((item, index) => {
                  const [createdDate, createdTime] = getDateTimeParts(item.created_on);
                  return (
                    <tr key={item.id}>
                    <td> {index + 1 + (params?.page - 1) * 10}</td>
                    <td>{item.company_name}</td>
                    <td>
                      {item.invoice_type === 0
                        ? "Simplified"
                        : item.invoice_type === 1
                        ? "VAT"
                        : "Purchase"}
                    </td>
                    <td>{createdDate}</td>
                    <td>{createdTime}</td>
                    <td>{item.invoice_count}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    Invoice Reports Not Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <CommonPagination
        params={params}
        setParams={setParams}
        pagination_meta={reportsData && reportsData?.pagination_meta}
      />
    </Card>
  );
};

export default InvoiceReport;
